import { Injectable } from '@angular/core';
import { SafeAny } from '../types/generic.types';
import { ExpertScheduleTimeSlot, TimeSlotInactiveReason } from '@services/experts-schedule.service.types';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeUtils } from 'src/app/lib/date-time-utils';
import { LocationService } from '@services/location.service';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

export enum ApiErrorCode {
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
  InternalServerError = 'INTERNAL_SERVER_ERROR',
  BadRequest = 'BAD_REQUEST',
  Conflict = 'CONFLICT',
  Unauthorized = 'UNAUTHORIZED',
  Unknown = 'UNKNOWN',
  TokenUnauthorized = 'TOKEN_UNAUTHORIZED',
  PromoAlreadyApplied = 'PROMO_ALREADY_APPLIED',
  PromoAlreadyPlacedOtherAppointments = 'PROMO_ALREADY_PLACED_OTHER_APPOINTMENTS',
  PromoAlreadyUsedByClient = 'PROMO_ALREADY_USED_BY_CLIENT',
  PromoBookingChannel = 'PROMO_BOOKING_CHANNEL',
  PromoClientRequired = 'PROMO_CLIENT_REQUIRED',
  PromoCodeInvalid = 'PROMO_CODE_INVALID',
  PromoDateBetween = 'PROMO_DATE_BETWEEN',
  PromoDayOfWeek = 'PROMO_DAY_OF_WEEK',
  PromoFinished = 'PROMO_FINISHED',
  PromoMaxUsage = 'PROMO_MAX_USAGE',
  PromoMinCart = 'PROMO_MIN_CART',
  PromoNotStackable = 'PROMO_NOT_STACKABLE',
  PromoNotStarted = 'PROMO_NOT_STARTED',
  PromoTimeBetween = 'PROMO_TIME_BETWEEN',
  ClientBanned = 'CLIENT_BANNED',
  AppointmentDateConflict = 'APPOINTMENT_DATE_CONFLICT',
}

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  constructor(
    private translate: TranslateService,
    private dateTimeUtils: DateTimeUtils,
    private locationService: LocationService,
    private timeFormatPipe: TimeFormatPipe,
  ) {}

  extractApiError(error: SafeAny) {
    if (this.extractApiErrorCode(error) === ApiErrorCode.AppointmentDateConflict && error?.error?.data) {
      return this.extractDateConflictError(
        error.error.data as ExpertScheduleTimeSlot & { inactiveReason?: string; reason?: string },
      );
    }

    return error?.error?.message
      ? Array.isArray(error.error.message)
        ? error.error.message[0]
        : error.error.message.toString()
      : 'An unexpected error occurred.';
  }

  extractApiErrorCode(error: SafeAny) {
    return error?.error?.errorCode;
  }

  extractApiErrorMessages(error: SafeAny): string | string[] | undefined {
    const message = error?.error?.message;

    if (typeof message === 'string') {
      return message;
    }

    if (Array.isArray(message) && message.every((item): item is string => typeof item === 'string')) {
      return message;
    }

    return undefined;
  }

  private extractDateConflictError(errorData: ExpertScheduleTimeSlot & { inactiveReason?: string; reason?: string }) {
    const reason = (errorData.inactiveReason || errorData.reason || TimeSlotInactiveReason.SkipFromDisplay) as string;
    const location = this.locationService.getLocation();

    if (reason === 'max_advance' && location?.maxBookingTime) {
      return this.extractAdvanceBookingError(reason, location.maxBookingTime);
    }

    if (reason === 'min_advance' && location?.minBookingTime) {
      return this.extractAdvanceBookingError(reason, location.minBookingTime);
    }

    const selectedTime = this.timeFormatPipe.transform(errorData.startTime, location?.timeFormat, location?.timeZone);
    return this.translate.instant(`pages.select-date.api-error.${reason}`, { selectedTime });
  }

  private extractAdvanceBookingError(reason: 'max_advance' | 'min_advance', bookingLimit: number) {
    const duration = this.dateTimeUtils.convertMinutesToDuration(bookingLimit);
    const unitKey = `shared.units.${duration.unit}`;
    const unit = this.translate.instant(unitKey);

    return this.translate.instant(`pages.select-date.api-error.${reason}`, {
      amount: String(duration.amount),
      unit: unit === unitKey ? duration.unit : unit,
    });
  }
}
